package br.com.vvrefeicoes.portal.feature.juridico

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppButton
import br.com.vvrefeicoes.portal.core.ui.components.AppDatePickerField
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppModalCard
import br.com.vvrefeicoes.portal.core.ui.components.AppPagination
import br.com.vvrefeicoes.portal.core.ui.components.AppTextField
import br.com.vvrefeicoes.portal.core.ui.components.EmptyState
import br.com.vvrefeicoes.portal.core.ui.components.PortalButtonVariant
import br.com.vvrefeicoes.portal.core.ui.components.SearchableSelect
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppShapes
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import br.com.vvrefeicoes.portal.core.util.MoneyFormatter
import br.com.vvrefeicoes.portal.core.util.openExternalUrl
import com.composables.icons.lucide.Building2
import com.composables.icons.lucide.Download
import com.composables.icons.lucide.ExternalLink
import com.composables.icons.lucide.FileSpreadsheet
import com.composables.icons.lucide.FileText
import com.composables.icons.lucide.Link
import com.composables.icons.lucide.Lucide
import com.composables.icons.lucide.RefreshCw
import com.composables.icons.lucide.Search
import kotlin.math.roundToLong

private val processHeaders =
    listOf(
        "Empresa",
        "CNPJ",
        "Número do processo",
        "Tribunal",
        "Órgão/Vara",
        "Assunto",
        "Classe",
        "Status",
        "Autor",
        "Réu",
        "Juiz",
        "Esfera",
        "Rito",
        "Instância",
        "Estratégico",
        "Valor",
        "Última movimentação",
        "Unidades",
        "Link externo",
    )

@Composable
fun JuridicoProcessosScreen() {
    val processosController = rememberJuridicoProcessosController()
    var exportMenuExpanded by remember { mutableStateOf(false) }
    LaunchedEffect(processosController) { processosController.init() }

    AppScrollColumn(
        Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        footer = { AppFooter() },
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space20),
    ) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.SpaceBetween) {
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                Text("Oficiais e Tribunais", style = MaterialTheme.typography.headlineMedium)
                Text("Processos", style = MaterialTheme.typography.titleLarge, color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
            Column(horizontalAlignment = Alignment.End) {
                Text(
                    "Saldo Escavador: ${processosController.credits?.let { "R$ ${MoneyFormatter.formatCents((it * 100).roundToLong())}" } ?: "indisponível"}",
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                Text("Atualização automática via webhook", color = MaterialTheme.colorScheme.onSurfaceVariant)
            }
        }

        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8), verticalAlignment = Alignment.CenterVertically) {
            AppButton(
                if (processosController.syncingHistorical) "Sincronizando histórico..." else "Sincronizar histórico (${processosController.historicalSyncStatus.executionsToday}/3)",
                processosController::synchronizeHistorical,
                enabled = !processosController.syncingHistorical && processosController.historicalSyncStatus.canSynchronize,
                variant = PortalButtonVariant.Secondary,
                leadingIcon = Lucide.RefreshCw,
            )
            Box {
                AppButton("Exportar", { exportMenuExpanded = true }, variant = PortalButtonVariant.Outlined, leadingIcon = Lucide.Download)
                DropdownMenu(expanded = exportMenuExpanded, onDismissRequest = { exportMenuExpanded = false }) {
                    DropdownMenuItem(
                        text = { Text("Exportar Excel") },
                        onClick = {
                            exportMenuExpanded = false
                            processosController.exportCsv()
                        },
                        leadingIcon = { Icon(Lucide.FileSpreadsheet, contentDescription = null) },
                    )
                    DropdownMenuItem(
                        text = { Text("Exportar PDF") },
                        onClick = {
                            exportMenuExpanded = false
                            processosController.exportPdf()
                        },
                        leadingIcon = { Icon(Lucide.FileText, contentDescription = null) },
                    )
                }
            }
        }

        processosController.errorMessage?.let { Text(it, color = MaterialTheme.colorScheme.error) }
        ProcessFilters(processosController)
        ProcessSummary(processosController.allFilteredProcessos)
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            if (processosController.loading) {
                CircularProgressIndicator()
            } else if (processosController.processos.isEmpty()) {
                EmptyState(message = "Nenhum processo encontrado para os filtros informados.")
            } else {
                ProcessTable(processosController.processos, processosController, compact = maxWidth < 720.dp)
            }
        }
        AppPagination(processosController.page, processosController.totalPages, processosController::goToPage, Modifier.align(Alignment.CenterHorizontally))
    }

    processosController.processoParaUnidades?.let { processo ->
        UnitLinkDialog(processo, processosController)
    }
}

@Composable
private fun ProcessFilters(controller: JuridicoProcessosController) {
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        if (maxWidth < 720.dp) {
            Surface(shape = AppShapes.medium, tonalElevation = 1.dp, modifier = Modifier.fillMaxWidth()) {
                Column(Modifier.padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                    AppTextField(controller.numeroFiltro, { controller.numeroFiltro = it }, label = "Número do processo", modifier = Modifier.fillMaxWidth())
                    ProcessSelect("Tribunal", controller.tribunalOptions, controller.tribunalFiltro, { controller.tribunalFiltro = it }, Modifier.fillMaxWidth())
                    ProcessSelect("Status", controller.statusOptions, controller.statusFiltro, { controller.statusFiltro = it }, Modifier.fillMaxWidth())
                    AppDatePickerField(controller.dataMinimaFiltro.toLocalDateOrNull(), { controller.dataMinimaFiltro = it?.toString().orEmpty() }, label = "Data inicial", modifier = Modifier.fillMaxWidth(), optional = true)
                    AppDatePickerField(controller.dataMaximaFiltro.toLocalDateOrNull(), { controller.dataMaximaFiltro = it?.toString().orEmpty() }, label = "Data final", modifier = Modifier.fillMaxWidth(), optional = true)
                    FilterActions(controller)
                }
            }
        } else {
            Surface(shape = AppShapes.medium, tonalElevation = 1.dp, modifier = Modifier.fillMaxWidth()) {
                Column(Modifier.padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                        AppTextField(controller.numeroFiltro, { controller.numeroFiltro = it }, label = "Número do processo", modifier = Modifier.weight(1f))
                        ProcessSelect("Tribunal", controller.tribunalOptions, controller.tribunalFiltro, { controller.tribunalFiltro = it }, Modifier.weight(1f))
                        ProcessSelect("Status", controller.statusOptions, controller.statusFiltro, { controller.statusFiltro = it }, Modifier.weight(1f))
                    }
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                        AppDatePickerField(controller.dataMinimaFiltro.toLocalDateOrNull(), { controller.dataMinimaFiltro = it?.toString().orEmpty() }, label = "Data inicial", modifier = Modifier.weight(1f), optional = true)
                        AppDatePickerField(controller.dataMaximaFiltro.toLocalDateOrNull(), { controller.dataMaximaFiltro = it?.toString().orEmpty() }, label = "Data final", modifier = Modifier.weight(1f), optional = true)
                        FilterActions(controller)
                    }
                }
            }
        }
    }
}

@Composable
private fun ProcessSelect(
    label: String,
    options: List<String>,
    selected: String,
    onSelect: (String) -> Unit,
    modifier: Modifier,
) {
    val allOptions = listOf("Todos") + options
    SearchableSelect(
        leadingIcon = null,
        options = allOptions,
        selected = selected.ifBlank { "Todos" },
        onSelect = { value -> onSelect(value.takeUnless { it == "Todos" }.orEmpty()) },
        modifier = modifier,
        placeholder = label,
        searchable = false,
    )
}

@Composable
private fun FilterActions(controller: JuridicoProcessosController) {
    Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
        AppButton("Filtrar", controller::applyFilters, variant = PortalButtonVariant.Secondary, leadingIcon = Lucide.Search)
        AppButton("Limpar", {
            controller.numeroFiltro = ""
            controller.tribunalFiltro = ""
            controller.statusFiltro = ""
            controller.dataMinimaFiltro = ""
            controller.dataMaximaFiltro = ""
            controller.applyFilters()
        }, variant = PortalButtonVariant.Outlined)
    }
}

@Composable
private fun ProcessSummary(processos: List<br.com.vvrefeicoes.portal.domain.model.JuridicoProcesso>) {
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        val cards =
            listOf(
                Triple("Total exibido", processos.size.toString(), MaterialTheme.colorScheme.primaryContainer),
                Triple("Ativos", processos.count { it.status.contains("ativo", ignoreCase = true) || it.status.contains("atord", ignoreCase = true) }.toString(), MaterialTheme.colorScheme.secondaryContainer),
                Triple("Em andamento", processos.count { it.status.contains("andamento", ignoreCase = true) }.toString(), MaterialTheme.colorScheme.tertiaryContainer),
                Triple("Com restrições", processos.count { it.status.contains("restri", ignoreCase = true) }.toString(), MaterialTheme.colorScheme.errorContainer),
                Triple("Arquivados", processos.count { it.status.contains("arquiv", ignoreCase = true) }.toString(), MaterialTheme.colorScheme.surfaceVariant),
            )
        if (maxWidth < 720.dp) {
            Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                cards.forEach { SummaryCard(it.first, it.second, it.third, Modifier.fillMaxWidth()) }
            }
        } else {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                cards.forEach { SummaryCard(it.first, it.second, it.third, Modifier.weight(1f)) }
            }
        }
    }
}

@Composable
private fun SummaryCard(
    title: String,
    value: String,
    color: androidx.compose.ui.graphics.Color,
    modifier: Modifier,
) {
    Card(modifier = modifier, colors = CardDefaults.cardColors(containerColor = color), shape = AppShapes.medium) {
        Column(Modifier.padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
            Text(title, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
            Text(value, style = MaterialTheme.typography.headlineSmall)
        }
    }
}

@Composable
private fun ProcessTable(
    processos: List<br.com.vvrefeicoes.portal.domain.model.JuridicoProcesso>,
    controller: JuridicoProcessosController,
    compact: Boolean,
) {
    Surface(shape = MaterialTheme.shapes.medium, tonalElevation = 1.dp, modifier = Modifier.fillMaxWidth()) {
        if (compact) {
            Column(Modifier.padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                processos.forEach { process ->
                    Column(verticalArrangement = Arrangement.spacedBy(AppSpacing.space4)) {
                        Text("${process.empresaNome} · ${process.numeroCnj}", style = MaterialTheme.typography.titleSmall)
                        Text("${process.tribunal} · ${process.status} · ${process.assunto}", color = MaterialTheme.colorScheme.onSurfaceVariant)
                        Text("Unidades: ${process.unidades.joinToString { it.nome }.ifBlank { "Nenhuma" }}", color = MaterialTheme.colorScheme.onSurfaceVariant)
                        Row(horizontalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                            IconButton(onClick = { controller.openUnitLink(process) }) {
                                Icon(Lucide.Link, contentDescription = "Vincular unidades")
                            }
                            process.linkExterno?.takeIf(String::isNotBlank)?.let { url ->
                                IconButton(onClick = { openExternalUrl(url) }) {
                                    Icon(Lucide.ExternalLink, contentDescription = "Abrir link externo")
                                }
                            }
                        }
                    }
                }
            }
        } else {
            Column(Modifier.fillMaxWidth().padding(AppSpacing.space16), verticalArrangement = Arrangement.spacedBy(AppSpacing.space8)) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                    processHeaders.forEach { TableHeader(it) }
                }
                processos.forEach { process ->
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(AppSpacing.space12)) {
                        listOf(
                            process.empresaNome,
                            process.empresaCnpj,
                            process.numeroCnj,
                            process.tribunal,
                            process.orgaoVara,
                            process.assunto,
                            process.classe,
                            process.status,
                            process.autor,
                            process.reu,
                            process.juiz,
                            process.esfera,
                            process.rito,
                            process.instancia,
                            if (process.estrategico) "Sim" else "Não",
                            process.valorCausa?.toString().orEmpty(),
                            process.ultimaMovimentacao?.toBrazilianDate().orEmpty(),
                        ).forEach { TableHeader(it) }
                        UnitButton(process, controller, Modifier.weight(1f))
                        ExternalLinkCell(process.linkExterno, Modifier.weight(1f))
                    }
                }
            }
        }
    }
}

@Composable
private fun UnitButton(
    process: br.com.vvrefeicoes.portal.domain.model.JuridicoProcesso,
    controller: JuridicoProcessosController,
    modifier: Modifier,
) {
    Row(modifier, verticalAlignment = Alignment.CenterVertically) {
        Text(process.unidades.joinToString { it.nome }.ifBlank { "Nenhuma" }, style = MaterialTheme.typography.labelMedium)
        IconButton(onClick = { controller.openUnitLink(process) }) {
            Icon(Lucide.Link, contentDescription = "Vincular unidades")
        }
    }
}

@Composable
private fun ExternalLinkCell(
    url: String?,
    modifier: Modifier,
) {
    Row(modifier, verticalAlignment = Alignment.CenterVertically) {
        if (!url.isNullOrBlank()) {
            IconButton(onClick = { openExternalUrl(url) }) {
                Icon(Lucide.ExternalLink, contentDescription = "Abrir link externo")
            }
        }
    }
}

@Composable
private fun RowScope.TableHeader(text: String) {
    Text(text, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.weight(1f))
}

@Composable
private fun UnitLinkDialog(
    process: br.com.vvrefeicoes.portal.domain.model.JuridicoProcesso,
    controller: JuridicoProcessosController,
) {
    var selectedIds by remember(process.id) { mutableStateOf(process.unidades.map { it.id }.toSet()) }
    AppModalCard("Vincular unidades", controller::closeUnitLink) {
        if (controller.availableUnits.isEmpty()) {
            Text("Nenhuma unidade disponível.", color = MaterialTheme.colorScheme.onSurfaceVariant)
        } else {
            controller.availableUnits.forEach { company ->
                val unitId = company.organogramaUnidadeId
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Checkbox(
                        checked = unitId in selectedIds,
                        onCheckedChange = { checked ->
                            selectedIds = if (checked) selectedIds + unitId else selectedIds - unitId
                        },
                    )
                    Icon(Lucide.Building2, contentDescription = null, tint = MaterialTheme.colorScheme.onSurfaceVariant)
                    Text(company.name, modifier = Modifier.padding(start = AppSpacing.space8))
                }
            }
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            AppButton("Cancelar", controller::closeUnitLink, variant = PortalButtonVariant.Text)
            AppButton("Salvar", { controller.saveUnitLink(selectedIds.toList()) }, enabled = !controller.savingUnits)
        }
    }
}
